import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Menu,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import api from '@src/api/clients';
import fetchAllFilteredClientZips from './clientZipExport';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const PAGE_SIZE = 100;
const EMPTY_ZIP = {
  client: '',
  client_name: '',
  zip_code: '',
  zip: '',
  vertical: '',
  vertical_name: '',
  client_territory: '',
  territory_name: '',
  calendar: '',
  active: true,
};
const INITIAL_IMPORT_MAPPING = {
  zip: '',
  territory: '',
  vertical: '',
  calendar: '',
  active: '',
};

export default function ClientZipsPage() {
  const [clientZips, setClientZips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [territories, setTerritories] = useState([]);
  const [allVerticals, setAllVerticals] = useState([]);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importClientId, setImportClientId] = useState('');
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importApplyLoading, setImportApplyLoading] = useState(false);
  const [importSelectedVerticals, setImportSelectedVerticals] = useState([]);
  const [importApplyAllVerticals, setImportApplyAllVerticals] = useState(false);
  const [importSelectedTerritories, setImportSelectedTerritories] = useState([]);
  const [importTerritories, setImportTerritories] = useState([]);
  const [importVerticalOptions, setImportVerticalOptions] = useState([]);
  const [importError, setImportError] = useState('');
  const [overlapDialogOpen, setOverlapDialogOpen] = useState(false);
  const [overlapText, setOverlapText] = useState('');
  const [overlapResult, setOverlapResult] = useState(null);
  const [overlapLoading, setOverlapLoading] = useState(false);
  const [overlapError, setOverlapError] = useState('');
  const [overlapExcludeClients, setOverlapExcludeClients] = useState([]);
  const [filters, setFilters] = useState({
    client: '',
    vertical: '',
    zip: '',
    territory: '',
    active: 'true',
    is_filtered_out: '',
  });
  const [open, setOpen] = useState(false);
  const [currentZip, setCurrentZip] = useState(EMPTY_ZIP);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [territoryDialogOpen, setTerritoryDialogOpen] = useState(false);
  const [territoryRows, setTerritoryRows] = useState([]);
  const [territoryLoading, setTerritoryLoading] = useState(false);
  const [territoryError, setTerritoryError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkEditVerticalName, setBulkEditVerticalName] = useState('');
  const [bulkEditTerritoryName, setBulkEditTerritoryName] = useState('');
  const [bulkEditClearTerritory, setBulkEditClearTerritory] = useState(false);
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const [bulkEditError, setBulkEditError] = useState('');

  const {
    options: clientOptions,
    handleInputChange: handleClientInputChange,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();
  const [importColumnMapping, setImportColumnMapping] = useState(
    INITIAL_IMPORT_MAPPING
  );

  const sanitizedFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(
          ([, value]) => value !== '' && value !== null && value !== undefined
        )
      ),
    [filters]
  );
  const bulkFilterParams = useMemo(() => {
    const params = { ...sanitizedFilters };
    if (sanitizedFilters.client) {
      params.client_id = sanitizedFilters.client;
    }
    if (sanitizedFilters.vertical) {
      params.vertical_id = sanitizedFilters.vertical;
    }
    return params;
  }, [sanitizedFilters]);

  const fetchClientZips = async (pageParam = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageParam,
        page_size: PAGE_SIZE,
        ...sanitizedFilters,
      }).toString();
      const res = await api.request({ url: `dashboard/client-zips/?${params}`, method: 'GET' });
      setClientZips(res.data.results);
      setTotalPages(Math.ceil(res.data.count / PAGE_SIZE));
      setTotalCount(res.data.count);
    } catch (err) {
      console.error('Failed to fetch client zips:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientZips(page);
  }, [page]);

  useEffect(() => {
    api
      .request({ url: 'dashboard/verticals/', method: 'GET' })
      .then((res) => {
        const options = res.data.map((v) => ({ value: v.id, label: v.vertical }));
        setAllVerticals(options);
        setVerticalOptions(options);
      });
  }, []);

  const fetchTerritories = (clientId, setter = setTerritories) => {
    if (clientId) {
      api
        .request({
          url: `dashboard/client-territories/?client_id=${clientId}`,
          method: 'GET',
        })
        .then((res) =>
          setter(res.data.map((t) => ({ value: t.id, label: t.name })))
        );
    } else {
      setter([]);
    }
  };

  const fetchVerticals = (clientId, setter = setVerticalOptions) => {
    if (clientId) {
      api
        .request({
          url: `dashboard/client-zips/vertical_options/?client_id=${clientId}`,
          method: 'GET',
        })
        .then((res) => {
          if (res.data.length) {
            setter(res.data.map((v) => ({ value: v.id, label: v.vertical })));
          } else {
            setter(allVerticals);
          }
        });
    } else {
      setter(allVerticals);
    }
  };

  useEffect(() => {
    fetchTerritories(filters.client);
    setFilters((prev) => ({ ...prev, territory: '' }));
  }, [filters.client]);

  useEffect(() => {
    fetchVerticals(filters.client);
    setFilters((prev) => ({ ...prev, vertical: '' }));
  }, [filters.client, allVerticals]);

  useEffect(() => {
    if (!importDialogOpen) {
      return;
    }
    if (importClientId) {
      fetchTerritories(importClientId, setImportTerritories);
      fetchVerticals(importClientId, setImportVerticalOptions);
    } else {
      setImportTerritories([]);
      setImportVerticalOptions(allVerticals);
    }
  }, [importDialogOpen, importClientId, allVerticals]);

  useEffect(() => {
    if (!overlapDialogOpen) {
      setOverlapText('');
      setOverlapResult(null);
      setOverlapError('');
      setOverlapExcludeClients([]);
      setOverlapLoading(false);
    }
  }, [overlapDialogOpen]);

  useEffect(() => {
    const newPage = 1;
    setPage(newPage);
    fetchClientZips(newPage);
    clearSelections();
  }, [filters.client, filters.vertical, filters.territory, filters.active, filters.is_filtered_out]);

  const handleFilterChange = (field, value) =>
    setFilters((prev) => ({ ...prev, [field]: value }));

  const handleSearch = () => {
    const newPage = 1;
    setPage(newPage);
    fetchClientZips(newPage);
    clearSelections();
  };

  const extractMetaField = (meta = {}, field) => {
    const value = meta[field];
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return value;
  };

  const loadTerritorySummary = async () => {
    if (!filters.client) {
      setTerritoryError('Select a client to view territories.');
      setTerritoryRows([]);
      setTerritoryDialogOpen(true);
      return;
    }

    setTerritoryDialogOpen(true);
    setTerritoryLoading(true);
    setTerritoryError('');

    try {
      const baseFilters = { ...sanitizedFilters, page_size: 500 };
      const allZips = await fetchAllFilteredClientZips({ baseFilters });

      let territoryMeta = [];
      try {
        const res = await api.request({
          url: `dashboard/client-territories/?client_id=${filters.client}`,
          method: 'GET',
        });
        territoryMeta = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        territoryMeta = [];
      }

      const metaById = new Map(
        territoryMeta.map((t) => [t.id ?? t.name, t])
      );
      const metaByName = new Map(
        territoryMeta.map((t) => [(t.name || '').toLowerCase(), t])
      );

      const agg = new Map();

      allZips.forEach((zip) => {
        const key = zip.territory_id || zip.client_territory || zip.territory_name || 'Unassigned';
        const meta =
          metaById.get(key) ||
          metaByName.get((zip.territory_name || '').toLowerCase()) ||
          metaByName.get((zip.territory || '').toLowerCase()) ||
          {};

        if (!agg.has(key)) {
          agg.set(key, {
            id: key,
            name: meta.name || zip.territory_name || zip.territory || 'Unassigned',
            zipCount: 0,
            unfilteredCount: 0,
            verticals: new Set(),
            meta,
          });
        }
        const row = agg.get(key);
        row.zipCount += 1;
        if (zip.is_filtered_out === false) {
          row.unfilteredCount += 1;
        }
        if (zip.vertical_name) {
          row.verticals.add(zip.vertical_name);
        }
      });

      const rows = Array.from(agg.values())
        .map((row) => ({
          name: row.name,
          zipCount: row.zipCount,
          unfilteredCount: row.unfilteredCount,
          verticals: Array.from(row.verticals).sort().join(', ') || '-',
          min_household_income: extractMetaField(row.meta, 'min_household_income'),
          min_home_value: extractMetaField(row.meta, 'min_home_value'),
          income_floor_factor: extractMetaField(row.meta, 'income_floor_factor'),
          home_value_floor_factor: extractMetaField(row.meta, 'home_value_floor_factor'),
          min_home_ownership_percentage: extractMetaField(row.meta, 'min_home_ownership_percentage'),
          min_sfh_percentage: extractMetaField(row.meta, 'min_sfh_percentage'),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setTerritoryRows(rows);
    } catch (err) {
      console.error('Failed to load territory summary:', err);
      setTerritoryError('Failed to load territory summary.');
    } finally {
      setTerritoryLoading(false);
    }
  };

  const handleImportClick = () => {
    setImportError('');
    setImportPreview(null);
    setImportText('');
    setImportColumnMapping(INITIAL_IMPORT_MAPPING);
    setImportSelectedVerticals(filters.vertical ? [filters.vertical] : []);
    setImportSelectedTerritories(
      filters.territory ? [filters.territory] : []
    );
    setImportApplyAllVerticals(false);
    setImportClientId(filters.client || '');
    setImportDialogOpen(true);
  };

  const splitImportLine = (line) => {
    if (line.includes('\t')) {
      return line.split('\t').map((token) => token.trim());
    }
    return line.split(',').map((token) => token.trim());
  };

  const parseImportText = (text) => {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length);
    if (!lines.length) {
      return { columns: [], dataRows: [], hasHeader: false };
    }
    const headerTokens = splitImportLine(lines[0]);
    const headerCandidates = [
      'zip',
      'zip_code',
      'vertical',
      'vertical_name',
      'territory',
      'territory_name',
      'calendar',
      'calendar_name',
      'active',
    ];
    let header = null;
    let startIndex = 0;
    if (
      headerTokens.some((token) =>
        headerCandidates.includes(token.toLowerCase())
      )
    ) {
      header = headerTokens.map((token) => token.toLowerCase());
      startIndex = 1;
    }

    const columns = (header || headerTokens).map((token, index) => ({
      key: header ? header[index] : `col_${index}`,
      label: (headerTokens[index] || `Column ${index + 1}`).trim() || `Column ${index + 1}`,
    }));
    const dataRows = [];
    for (let i = startIndex; i < lines.length; i += 1) {
      const tokens = splitImportLine(lines[i]);
      dataRows.push(tokens);
    }
    return { columns, dataRows, hasHeader: Boolean(header) };
  };

  const buildDefaultColumnMapping = (columns) => {
    const mapping = { ...INITIAL_IMPORT_MAPPING };
    const matchers = {
      zip: ['zip', 'zip_code', 'zipcode', 'postal'],
      territory: ['territory', 'territory_name', 'client_territory'],
      vertical: ['vertical', 'vertical_name'],
      calendar: ['calendar', 'calendar_name'],
      active: ['active', 'status'],
    };
    const normalizedColumns = columns.map((col) => ({
      ...col,
      normalizedKey: (col.key || '').toLowerCase(),
      normalizedLabel: (col.label || '').toLowerCase(),
    }));

    Object.entries(matchers).forEach(([field, keys]) => {
      const found = normalizedColumns.find((col) =>
        keys.some(
          (key) =>
            col.normalizedKey === key ||
            col.normalizedLabel === key ||
            col.normalizedLabel.includes(`${key} `)
        )
      );
      if (found) {
        mapping[field] = found.key;
      }
    });
    return mapping;
  };

  const mapRowsWithColumns = (
    dataRows,
    columns,
    columnMapping = null
  ) => {
    if (!dataRows.length || !columns.length) return [];
    const defaultMapping = buildDefaultColumnMapping(columns);
    const mapping = {
      ...defaultMapping,
      ...(columnMapping && Object.values(columnMapping).some(Boolean)
        ? Object.fromEntries(
            Object.entries(columnMapping).filter(([, value]) => Boolean(value))
          )
        : {}),
    };

    const columnIndex = new Map(columns.map((col, index) => [col.key, index]));
    const getValue = (rowTokens, fieldKey) => {
      const mappedKey = mapping[fieldKey];
      if (!mappedKey || !columnIndex.has(mappedKey)) return '';
      return (rowTokens[columnIndex.get(mappedKey)] ?? '').trim();
    };

    return dataRows.map((tokens) => ({
      zip_code: getValue(tokens, 'zip'),
      territory_name: getValue(tokens, 'territory'),
      vertical_raw: getValue(tokens, 'vertical'),
      calendar: getValue(tokens, 'calendar'),
      active: getValue(tokens, 'active'),
    }));
  };

  const { columns: importColumns, dataRows: importDataRows } = useMemo(
    () => parseImportText(importText),
    [importText]
  );
  const defaultImportColumnMapping = useMemo(
    () => buildDefaultColumnMapping(importColumns),
    [importColumns]
  );
  const mappedImportRows = useMemo(
    () => mapRowsWithColumns(importDataRows, importColumns, importColumnMapping),
    [importDataRows, importColumns, importColumnMapping]
  );

  useEffect(() => {
    if (!importColumns.length) {
      setImportColumnMapping(INITIAL_IMPORT_MAPPING);
      return;
    }
    setImportColumnMapping((prev) => {
      const validKeys = new Set(importColumns.map((col) => col.key));
      const next = { ...prev };

      Object.entries(next).forEach(([field, key]) => {
        if (key && !validKeys.has(key)) {
          next[field] = '';
        }
      });

      Object.entries(defaultImportColumnMapping).forEach(([field, key]) => {
        if (!next[field] && key) {
          next[field] = key;
        }
      });

      return next;
    });
  }, [importColumns, defaultImportColumnMapping]);

  const buildImportPayload = () => {
    if (!importColumnMapping.zip) {
      setImportError('Select which column contains ZIP codes.');
      return null;
    }
    if (!importDataRows.length || !mappedImportRows.length) {
      return null;
    }
    const rows = mappedImportRows.map((row) => {
      const payloadRow = {
        zip_code: row.zip_code,
        territory_name: row.territory_name,
        calendar: row.calendar,
      };

      if (row.vertical_raw) {
        const names = row.vertical_raw
          .replace(/\|/g, ';')
          .split(';')
          .map((name) => name.trim())
          .filter(Boolean);
        if (names.length === 1) {
          [payloadRow.vertical] = names;
        } else if (names.length > 1) {
          payloadRow.verticals = names;
        }
      }

      if (row.active !== '') {
        payloadRow.active = row.active;
      }

      return payloadRow;
    });

    const payload = {
      client_id: importClientId,
      rows,
      apply_to_all_verticals: importApplyAllVerticals,
    };

    if (importSelectedVerticals.length) {
      payload.default_vertical_ids = importSelectedVerticals;
      payload.scope_vertical_ids = importSelectedVerticals;
    }
    if (importSelectedTerritories.length) {
      payload.scope_territory_ids = importSelectedTerritories;
    }

    if (!payload.default_vertical_ids?.length) {
      delete payload.default_vertical_ids;
    }
    if (!payload.scope_vertical_ids?.length) {
      delete payload.scope_vertical_ids;
    }
    if (!payload.scope_territory_ids?.length) {
      delete payload.scope_territory_ids;
    }

    return { payload, rows };
  };

  const buildOverlapRows = () => {
    const { columns, dataRows } = parseImportText(overlapText);
    const parsedRows = mapRowsWithColumns(dataRows, columns);
    if (!parsedRows.length) {
      return [];
    }
    return parsedRows.map((row) => {
      const payloadRow = {
        zip_code: row.zip_code,
        territory_name: row.territory_name,
      };

      if (row.vertical_raw) {
        const names = row.vertical_raw
          .replace(/\|/g, ';')
          .split(';')
          .map((name) => name.trim())
          .filter(Boolean);
        if (names.length === 1) {
          [payloadRow.vertical] = names;
        } else if (names.length > 1) {
          payloadRow.verticals = names;
        }
      }

      return payloadRow;
    });
  };

  const formatNumber = (value) =>
    new Intl.NumberFormat('en-US').format(value ?? 0);
  const formatPercent = (value) => `${Number(value ?? 0).toFixed(2)}%`;

  const handleOverlapClick = () => {
    setOverlapError('');
    setOverlapResult(null);
    setOverlapText('');
    setOverlapExcludeClients(filters.client ? [filters.client] : []);
    setOverlapDialogOpen(true);
  };

  const handleOverlapDialogClose = () => {
    setOverlapDialogOpen(false);
  };

  const handleOverlapAnalyze = async () => {
    setOverlapError('');
    const rows = buildOverlapRows();
    if (!rows.length) {
      setOverlapError('Add at least one ZIP row to analyze.');
      return;
    }
    setOverlapLoading(true);
    try {
      const payload = { rows };
      if (overlapExcludeClients.length) {
        payload.exclude_client_ids = overlapExcludeClients;
      }
      const res = await api.request({
        url: 'dashboard/client-zips/overlap-analysis/',
        method: 'POST',
        payload,
      });
      setOverlapResult(res.data);
    } catch (err) {
      console.error('Failed to analyze overlap:', err);
      setOverlapError('Failed to analyze overlap.');
    } finally {
      setOverlapLoading(false);
    }
  };

  const handlePreviewImport = async () => {
    setImportError('');
    if (!importClientId) {
      setImportError('Select a client before importing.');
      return;
    }
    const built = buildImportPayload();
    if (!built) {
      return;
    }
    if (!built.payload.rows.length) {
      setImportError('Add at least one ZIP row to preview.');
      return;
    }

    setImportLoading(true);
    try {
      const res = await api.request({
        url: 'dashboard/client-zips/preview-import/',
        method: 'POST',
        payload: built.payload,
      });
      setImportPreview(res.data);
    } catch (err) {
      console.error('Failed to preview import:', err);
      const detail = err?.response?.data?.detail;
      const rowsError = err?.response?.data?.rows;
      const message =
        (Array.isArray(rowsError) && rowsError.join(', ')) ||
        detail ||
        'Failed to preview import.';
      setImportError(String(message));
    } finally {
      setImportLoading(false);
    }
  };

  const handleApplyImport = async () => {
    setImportError('');
    if (!importClientId) {
      setImportError('Select a client before importing.');
      return;
    }
    const built = buildImportPayload();
    if (!built) {
      return;
    }
    if (!built.payload.rows.length) {
      setImportError('Add at least one ZIP row to import.');
      return;
    }

    setImportApplyLoading(true);
    try {
      const res = await api.request({
        url: 'dashboard/client-zips/apply-import/',
        method: 'POST',
        payload: built.payload,
      });
      setImportPreview(res.data);
      setSnackbar({
        open: true,
        message: 'Client ZIP import completed.',
        severity: 'success',
      });
      setPage(1);
      fetchClientZips(1);
    } catch (err) {
      console.error('Failed to apply import:', err);
      const detail = err?.response?.data?.detail;
      const rowsError = err?.response?.data?.rows;
      const message =
        (Array.isArray(rowsError) && rowsError.join(', ')) ||
        detail ||
        'Failed to apply import.';
      setImportError(String(message));
    } finally {
      setImportApplyLoading(false);
    }
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
    setImportLoading(false);
    setImportApplyLoading(false);
    setImportColumnMapping(INITIAL_IMPORT_MAPPING);
  };

  const importSummary = useMemo(
    () => ({
      create: importPreview?.summary?.create ?? 0,
      update: importPreview?.summary?.update ?? 0,
      deactivate: importPreview?.summary?.deactivate ?? 0,
      skip: importPreview?.summary?.skip ?? 0,
    }),
    [importPreview]
  );

  const previewSamples = useMemo(
    () => ({
      creates: (importPreview?.creates ?? []).slice(0, 5),
      updates: (importPreview?.updates ?? []).slice(0, 5),
      deactivations: (importPreview?.deactivations ?? []).slice(0, 5),
      skipped: (importPreview?.skipped ?? []).slice(0, 5),
    }),
    [importPreview]
  );

  const handleToggleActive = (zip) => {
    api
      .request({
        url: `dashboard/client-zips/${zip.id}/`,
        method: 'PATCH',
        payload: { active: !zip.active },
      })
      .then(() => fetchClientZips(page))
      .catch((err) => console.error('Failed to update zip:', err));
  };

  const handleEditOpen = (zip) => {
    fetchTerritories(zip.client);
    setCurrentZip(zip);
    setOpen(true);
  };
  const handleEditClose = () => {
    setOpen(false);
    setCurrentZip(EMPTY_ZIP);
    fetchTerritories(filters.client);
  };
  const handleEditChange = (field, value) =>
    setCurrentZip((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    const url = `dashboard/client-zips/${currentZip.id}/`;
    const payload = {
      client: currentZip.client,
      zip_code: currentZip.zip_code,
      vertical: currentZip.vertical,
      client_territory: currentZip.client_territory || null,
      calendar: currentZip.calendar,
      active: currentZip.active,
    };
    api
      .request({ url, method: 'PATCH', payload })
      .then(() => {
        handleEditClose();
        fetchClientZips(page);
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Failed to save client zip',
          severity: 'error',
        })
      );
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this zip?')) return;
    api
      .request({ url: `dashboard/client-zips/${id}/`, method: 'DELETE' })
      .then(() => fetchClientZips(page))
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Failed to delete client zip',
          severity: 'error',
        })
      );
  };

  const clearSelections = () => {
    setSelectedIds([]);
    setSelectAllFiltered(false);
  };

  const handleSelectAllClick = (checked) => {
    if (checked) {
      setSelectedIds(clientZips.map((z) => z.id));
      setSelectAllFiltered(false);
    } else {
      clearSelections();
    }
  };

  const handleSelect = (id) => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedIds(clientZips.map((z) => z.id).filter((zid) => zid !== id));
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      );
    }
  };

  const handleSelectAllFiltered = () => {
    setSelectAllFiltered(true);
    setSelectedIds([]);
  };

  const buildBulkRequestConfig = () => {
    if (selectAllFiltered) {
      return {
        payload: { apply_filters: true },
        options: { params: bulkFilterParams },
      };
    }
    return { payload: { ids: selectedIds }, options: {} };
  };

  const handleBulkUpdateActive = async (shouldActivate) => {
    if (!selectAllFiltered && selectedIds.length === 0) {
      return;
    }
    setBulkUpdating(true);
    const { payload, options } = buildBulkRequestConfig();
    try {
      await api.request({
        url: shouldActivate
          ? 'dashboard/client-zips/bulk_activate/'
          : 'dashboard/client-zips/bulk_deactivate/',
        method: 'POST',
        payload,
        options,
      });
      clearSelections();
      fetchClientZips(page);
      setSnackbar({
        open: true,
        message: shouldActivate ? 'Selected zips activated' : 'Selected zips deactivated',
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to update zip status:', err);
      setSnackbar({
        open: true,
        message: shouldActivate ? 'Failed to activate zips' : 'Failed to deactivate zips',
        severity: 'error',
      });
    } finally {
      setBulkUpdating(false);
      setBulkMenuAnchor(null);
    }
  };

  const handleBulkEditOpen = () => {
    setBulkEditError('');
    setBulkEditVerticalName('');
    setBulkEditTerritoryName('');
    setBulkEditClearTerritory(false);
    setBulkEditDialogOpen(true);
    setBulkMenuAnchor(null);
  };

  const handleBulkEditClose = () => {
    setBulkEditDialogOpen(false);
    setBulkEditSaving(false);
    setBulkEditError('');
    setBulkEditVerticalName('');
    setBulkEditTerritoryName('');
    setBulkEditClearTerritory(false);
  };

  const handleBulkEditSubmit = async () => {
    setBulkEditError('');
    if (!selectAllFiltered && selectedIds.length === 0) {
      setBulkEditError('Select at least one ZIP before applying bulk changes.');
      return;
    }
    const updates = {};
    const verticalName = bulkEditVerticalName.trim();
    const territoryName = bulkEditTerritoryName.trim();

    if (verticalName) {
      updates.vertical_name = verticalName;
    }
    if (bulkEditClearTerritory) {
      updates.client_territory_id = null;
    } else if (territoryName) {
      updates.client_territory_name = territoryName;
    }
    if (Object.keys(updates).length === 0) {
      setBulkEditError('Choose a vertical and/or territory change to apply.');
      return;
    }

    const { payload, options } = buildBulkRequestConfig();
    const requestPayload = { ...payload, ...updates };

    setBulkEditSaving(true);
    try {
      const res = await api.request({
        url: 'dashboard/client-zips/bulk-update/',
        method: 'POST',
        payload: requestPayload,
        options,
      });
      const updatedCount =
        res?.data?.updated ??
        (selectAllFiltered ? totalCount : selectedIds.length);
      setSnackbar({
        open: true,
        message: `Updated ${updatedCount} ZIP${updatedCount === 1 ? '' : 's'}.`,
        severity: 'success',
      });
      clearSelections();
      fetchClientZips(page);
      handleBulkEditClose();
    } catch (err) {
      console.error('Failed to bulk update zips:', err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0];
      setBulkEditError(detail || 'Failed to update selected ZIPs.');
    } finally {
      setBulkEditSaving(false);
    }
  };

  const formatDateDisplay = (utc) =>
    utc
      ? new Date(utc).toLocaleString('en-US', {
          timeZone: 'America/New_York',
        })
      : '-';

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (selectAllFiltered) {
        params.set('apply_filters', 'true');
        Object.entries(bulkFilterParams).forEach(([key, value]) => {
          params.set(key, value);
        });
      } else if (selectedIds.length) {
        selectedIds.forEach((id) => params.append('ids', id));
      } else {
        return;
      }

      const response = await api.request({
        url: `dashboard/client-zips/export/?${params.toString()}`,
        method: 'GET',
        options: { responseType: 'blob' },
      });

      const blobData =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blobData);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'client_zips.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      setSnackbar({
        open: true,
        message: 'Failed to export CSV.',
        severity: 'error',
      });
    }
  };

  const selectedOverlapExcludeOptions = overlapExcludeClients
    .map((id) => findClientOptionByValue(id))
    .filter(Boolean);
  const overlapSummary = overlapResult?.input_summary || null;
  const overlapVerticals = overlapSummary?.verticals ?? [];
  const overlapTerritories = overlapResult?.territory_breakdown ?? [];
  const overlapClients = overlapResult?.clients ?? [];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Client Zips</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={handleOverlapClick}>
            Compare Overlap
          </Button>
          <Button variant="contained" onClick={handleImportClick}>
            Import ZIPs
          </Button>
        </Stack>
      </Box>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={findClientOptionByValue(filters.client)}
              onChange={(e, val) => handleFilterChange('client', val ? val.value : '')}
              onInputChange={handleClientInputChange}
              renderInput={(params) => (
                <TextField {...params} label="Client" size="small" sx={{ minWidth: 200 }} />
              )}
            />
            <Autocomplete
              options={verticalOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={verticalOptions.find((v) => v.value === filters.vertical) || null}
              onChange={(e, val) =>
                handleFilterChange('vertical', val ? val.value : '')
              }
              renderInput={(params) => (
                <TextField {...params} label="Vertical" size="small" sx={{ minWidth: 200 }} />
              )}
            />
            <Autocomplete
              options={territories}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={territories.find((t) => t.value === filters.territory) || null}
              onChange={(e, val) =>
                handleFilterChange('territory', val ? val.value : '')
              }
              renderInput={(params) => (
                <TextField {...params} label="Territory" size="small" sx={{ minWidth: 200 }} />
              )}
            />
            <TextField
              label="ZIP"
              value={filters.zip}
              onChange={(e) => handleFilterChange('zip', e.target.value)}
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.active}
                onChange={(e) => handleFilterChange('active', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Filtered out</InputLabel>
              <Select
                label="Filtered out"
                value={filters.is_filtered_out}
                onChange={(e) => handleFilterChange('is_filtered_out', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleSearch}>
              Search ZIP
            </Button>
            <Button
              variant="outlined"
              onClick={loadTerritorySummary}
              disabled={!filters.client}
            >
              See territories
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {(selectedIds.length > 0 || selectAllFiltered) && (
                <Toolbar
                  sx={{
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                    bgcolor: 'action.hover',
                    mb: 1,
                  }}
                >
                  <Typography sx={{ flex: '1 1 100%' }} variant="subtitle1" component="div">
                    {selectAllFiltered
                      ? `All ${totalCount} selected`
                      : `${selectedIds.length} selected`}
                  </Typography>
                  <Button variant="contained" onClick={handleExportCsv} sx={{ mr: 1 }}>
                    Export CSV
                  </Button>
                  <Button
                    variant="contained"
                    onClick={(event) => setBulkMenuAnchor(event.currentTarget)}
                    disabled={bulkUpdating}
                  >
                    Bulk Actions
                  </Button>
                  <Menu
                    anchorEl={bulkMenuAnchor}
                    open={Boolean(bulkMenuAnchor)}
                    onClose={() => setBulkMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={handleBulkEditOpen}
                      disabled={bulkUpdating}
                    >
                      Update Vertical / Territory
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleBulkUpdateActive(true)}
                      disabled={bulkUpdating}
                    >
                      Activate Selected
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleBulkUpdateActive(false)}
                      disabled={bulkUpdating}
                    >
                      Deactivate Selected
                    </MenuItem>
                  </Menu>
                </Toolbar>
              )}
              {selectedIds.length === clientZips.length &&
                totalCount > clientZips.length &&
                !selectAllFiltered && (
                  <Box p={2} pt={0}>
                    <Typography variant="body2">
                      All {clientZips.length} items on this page are selected.{' '}
                      <Button size="small" onClick={handleSelectAllFiltered}>
                        Select all {totalCount} items
                      </Button>
                    </Typography>
                  </Box>
                )}
              {selectAllFiltered && totalCount > clientZips.length && (
                <Box p={2} pt={0}>
                  <Typography variant="body2">
                    All {totalCount} items that match current filters are selected.
                  </Typography>
                </Box>
              )}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate=
                            {!selectAllFiltered &&
                              selectedIds.length > 0 &&
                              selectedIds.length < clientZips.length}
                          checked=
                            {selectAllFiltered ||
                              (clientZips.length > 0 &&
                                selectedIds.length === clientZips.length)}
                          onChange={(e) => handleSelectAllClick(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>ZIP</TableCell>
                      <TableCell>Vertical</TableCell>
                      <TableCell>Territory</TableCell>
                      <TableCell>Calendar</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell>Is filtered out</TableCell>
                      <TableCell>Date Created</TableCell>
                      <TableCell>Date Updated</TableCell>
                      <TableCell>Date Deactivated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientZips.map((zip) => (
                      <TableRow
                        key={zip.id}
                        selected={selectAllFiltered || selectedIds.includes(zip.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectAllFiltered || selectedIds.includes(zip.id)}
                            onChange={() => handleSelect(zip.id)}
                          />
                        </TableCell>
                        <TableCell>{zip.client_name}</TableCell>
                        <TableCell>{zip.zip}</TableCell>
                        <TableCell>{zip.vertical_name}</TableCell>
                        <TableCell>{zip.territory_name || '-'}</TableCell>
                        <TableCell>{zip.calendar || '-'}</TableCell>
                        <TableCell>
                          <Switch
                            checked={zip.active}
                            onChange={() => handleToggleActive(zip)}
                          />
                        </TableCell>
                        <TableCell>{zip.is_filtered_out ? '✅' : '❌'}</TableCell>
                        <TableCell>{formatDateDisplay(zip.created_at)}</TableCell>
                        <TableCell>{formatDateDisplay(zip.updated_at)}</TableCell>
                        <TableCell>{formatDateDisplay(zip.deactivated_at)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => handleEditOpen(zip)}>
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleDelete(zip.id)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={bulkEditDialogOpen}
        onClose={handleBulkEditClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Edit Selected ZIPs</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {bulkEditError && <Alert severity="error">{bulkEditError}</Alert>}
            <Typography variant="body2" color="text.secondary">
              {selectAllFiltered
                ? `Applies to all ${totalCount} ZIPs that match the current filters.`
                : `Applies to ${selectedIds.length} selected ZIP${
                    selectedIds.length === 1 ? '' : 's'
                  }.`}
            </Typography>
            <Autocomplete
              freeSolo
              options={verticalOptions.map((v) => v.label)}
              value={bulkEditVerticalName}
              onInputChange={(e, val) => setBulkEditVerticalName(val || '')}
              onChange={(e, val) => setBulkEditVerticalName(val || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vertical name (optional)"
                  helperText="Type a vertical name. Must match an existing global vertical."
                />
              )}
            />
            <Autocomplete
              freeSolo
              options={territories.map((t) => t.label)}
              value={bulkEditTerritoryName}
              onInputChange={(e, val) => setBulkEditTerritoryName(val || '')}
              onChange={(e, val) => setBulkEditTerritoryName(val || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Territory name (optional)"
                  helperText="Type a territory name to reuse or create it for each client in the selection."
                />
              )}
              disabled={bulkEditClearTerritory}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkEditClearTerritory}
                  onChange={(e) => setBulkEditClearTerritory(e.target.checked)}
                />
              }
              label="Clear territory assignment"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkEditClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkEditSubmit}
            disabled={bulkEditSaving}
            startIcon={bulkEditSaving ? <CircularProgress size={16} /> : null}
          >
            Apply Bulk Edit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={territoryDialogOpen}
        onClose={() => setTerritoryDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Territories summary</DialogTitle>
        <DialogContent dividers>
          {territoryLoading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : territoryError ? (
            <Alert severity="error">{territoryError}</Alert>
          ) : territoryRows.length === 0 ? (
            <Typography variant="body2">No territories found for the current filters.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Territory</TableCell>
                    <TableCell>ZIPs</TableCell>
                    <TableCell>Unfiltered ZIPs</TableCell>
                    <TableCell>Verticals</TableCell>
                    <TableCell>Min household income</TableCell>
                    <TableCell>Min home value</TableCell>
                    <TableCell>Income floor factor</TableCell>
                    <TableCell>Home value floor factor</TableCell>
                    <TableCell>Min home ownership %</TableCell>
                    <TableCell>Min SFH %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {territoryRows.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.zipCount}</TableCell>
                      <TableCell>{row.unfilteredCount}</TableCell>
                      <TableCell>{row.verticals}</TableCell>
                      <TableCell>{row.min_household_income}</TableCell>
                      <TableCell>{row.min_home_value}</TableCell>
                      <TableCell>{row.income_floor_factor}</TableCell>
                      <TableCell>{row.home_value_floor_factor}</TableCell>
                      <TableCell>{row.min_home_ownership_percentage}</TableCell>
                      <TableCell>{row.min_sfh_percentage}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerritoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={overlapDialogOpen}
        onClose={handleOverlapDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Compare ZIP Overlap</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {overlapError && <Alert severity="error">{overlapError}</Alert>}
            <Autocomplete
              multiple
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={selectedOverlapExcludeOptions}
              onChange={(e, values) =>
                setOverlapExcludeClients(values.map((v) => v.value))
              }
              onInputChange={handleClientInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Exclude Clients"
                  placeholder="Select clients to ignore"
                />
              )}
            />
            <TextField
              label="Paste ZIP data"
              value={overlapText}
              onChange={(e) => setOverlapText(e.target.value)}
              multiline
              minRows={6}
              placeholder="zip,territory,vertical"
            />
            {overlapLoading && (
              <Box display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            )}
            {overlapResult && !overlapLoading && (
              <Stack spacing={3}>
                {overlapSummary && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Overall Summary
                    </Typography>
                    {overlapSummary.overlap_zip_verticals === 0 ? (
                      <Alert severity="info">
                        No overlaps were found for the provided ZIP codes and
                        verticals.
                      </Alert>
                    ) : (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={3}
                      >
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Total ZIP / Vertical Combinations
                          </Typography>
                          <Typography variant="h6">
                            {formatNumber(
                              overlapSummary.total_zip_verticals
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Overlapping ZIP / Verticals
                          </Typography>
                          <Typography variant="h6">
                            {formatNumber(
                              overlapSummary.overlap_zip_verticals
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Percent Overlap
                          </Typography>
                          <Typography variant="h6">
                            {formatPercent(
                              overlapSummary.overlap_percentage
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Overlap Population
                          </Typography>
                          <Typography variant="h6">
                            {formatNumber(overlapSummary.overlap_population)}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                )}

                {overlapVerticals.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Vertical Breakdown
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Vertical</TableCell>
                            <TableCell align="right">Total ZIPs</TableCell>
                            <TableCell align="right">Overlap ZIPs</TableCell>
                            <TableCell align="right">Overlap %</TableCell>
                            <TableCell align="right">Overlap Population</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overlapVerticals.map((vertical) => (
                            <TableRow
                              key={`vertical-${vertical.vertical_id ?? vertical.vertical_name ?? 'unknown'}`}
                            >
                              <TableCell>{vertical.vertical_name}</TableCell>
                              <TableCell align="right">
                                {formatNumber(vertical.total_zip_verticals)}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(vertical.overlap_zip_verticals)}
                              </TableCell>
                              <TableCell align="right">
                                {formatPercent(vertical.overlap_percentage)}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(vertical.overlap_population)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {overlapTerritories.length > 0 && (
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Territory Breakdown
                    </Typography>
                    {overlapTerritories.map((territory) => (
                      <Paper
                        key={territory.territory_name || 'unassigned'}
                        sx={{ p: 2 }}
                        variant="outlined"
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {territory.territory_name || 'Unassigned'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {`${formatNumber(territory.overlap_zip_verticals)} of ${formatNumber(
                            territory.total_zip_verticals
                          )} ZIP / vertical combinations overlap (${formatPercent(
                            territory.overlap_percentage
                          )}).`}
                        </Typography>
                        {territory.verticals?.length ? (
                          <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Vertical</TableCell>
                                <TableCell align="right">Total ZIPs</TableCell>
                                <TableCell align="right">Overlap ZIPs</TableCell>
                                <TableCell align="right">Overlap %</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {territory.verticals.map((vertical) => (
                                <TableRow
                                  key={`territory-${territory.territory_name || 'unassigned'}-${vertical.vertical_id ?? vertical.vertical_name}`}
                                >
                                  <TableCell>{vertical.vertical_name}</TableCell>
                                  <TableCell align="right">
                                    {formatNumber(vertical.total_zip_verticals)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(vertical.overlap_zip_verticals)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatPercent(vertical.overlap_percentage)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : null}
                      </Paper>
                    ))}
                  </Stack>
                )}

                {overlapClients.length > 0 && (
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Client Overlap
                    </Typography>
                    {overlapClients.map((client) => (
                      <Paper key={client.client_id} sx={{ p: 2 }} variant="outlined">
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          {client.client_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {`${formatNumber(client.overlap_zip_verticals)} overlapping ZIP / vertical combinations – ${formatPercent(
                            client.overlap_percentage_of_input
                          )} of input, ${formatPercent(
                            client.overlap_percentage_of_client
                          )} of client coverage.`}
                        </Typography>
                        {client.verticals?.length ? (
                          <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Vertical</TableCell>
                                <TableCell align="right">Overlap ZIPs</TableCell>
                                <TableCell align="right">% of Input Vertical</TableCell>
                                <TableCell align="right">% of Client Vertical</TableCell>
                                <TableCell align="right">Overlap Population</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {client.verticals.map((vertical) => (
                                <TableRow key={`${client.client_id}-${vertical.vertical_id}`}>
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2">
                                        {vertical.vertical_name}
                                      </Typography>
                                      {vertical.territories?.length ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          component="div"
                                        >
                                          {vertical.territories
                                            .map(
                                              (territory) =>
                                                `${territory.territory_name || 'Unassigned'}: ${formatNumber(
                                                  territory.overlap_zip_verticals
                                                )} (${formatPercent(
                                                  territory.overlap_percentage_of_client_territory
                                                )})`
                                            )
                                            .join('; ')}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(vertical.overlap_zip_verticals)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatPercent(
                                      vertical.overlap_percentage_of_input_vertical
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatPercent(
                                      vertical.overlap_percentage_of_client_vertical
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(vertical.overlap_population)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : null}
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverlapDialogClose}>Close</Button>
          <Button
            variant="contained"
            onClick={handleOverlapAnalyze}
            disabled={overlapLoading}
            startIcon={overlapLoading ? <CircularProgress size={16} /> : null}
          >
            Analyze Overlap
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={handleImportDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Client ZIPs</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {importError && (
              <Alert severity="error">{importError}</Alert>
            )}
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={findClientOptionByValue(importClientId)}
              onChange={(e, val) => setImportClientId(val ? val.value : '')}
              onInputChange={handleClientInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  size="small"
                  sx={{ minWidth: 240 }}
                />
              )}
            />
            <Autocomplete
              multiple
              options={importVerticalOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={importVerticalOptions.filter((option) =>
                importSelectedVerticals.includes(option.value)
              )}
              onChange={(e, values) =>
                setImportSelectedVerticals(values.map((v) => v.value))
              }
              disabled={importApplyAllVerticals}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Apply to Verticals"
                  placeholder="Select verticals"
                />
              )}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={importApplyAllVerticals}
                  onChange={(e) => setImportApplyAllVerticals(e.target.checked)}
                />
              }
              label="Apply rows to all verticals associated with this client"
            />
            <Autocomplete
              multiple
              options={importTerritories}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={importTerritories.filter((option) =>
                importSelectedTerritories.includes(option.value)
              )}
              onChange={(e, values) =>
                setImportSelectedTerritories(values.map((v) => v.value))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Limit diff to territories"
                  placeholder="Leave empty to use detected territories"
                />
              )}
            />
            <TextField
              label="Paste ZIP data"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              multiline
              minRows={8}
              placeholder="zip,territory,vertical,calendar,active"
            />
            {importColumns.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Column Mapping
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Choose which pasted column should populate each field.
                </Typography>
                <Stack spacing={2}>
                  {[
                    { key: 'zip', label: 'ZIP Code', required: true },
                    { key: 'territory', label: 'Territory' },
                    { key: 'vertical', label: 'Vertical' },
                    { key: 'calendar', label: 'Calendar' },
                    { key: 'active', label: 'Active' },
                  ].map((field) => (
                    <TextField
                      key={field.key}
                      select
                      fullWidth
                      size="small"
                      label={`${field.label}${field.required ? ' *' : ''}`}
                      value={importColumnMapping[field.key] || ''}
                      onChange={(e) =>
                        setImportColumnMapping((prev) => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      helperText={
                        field.required
                          ? 'Required for preview and apply.'
                          : 'Optional; leave blank to skip.'
                      }
                    >
                      <MenuItem value="">
                        <em>Not mapped</em>
                      </MenuItem>
                      {importColumns.map((col) => (
                        <MenuItem key={col.key} value={col.key}>
                          {col.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ))}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mt={1}
                  display="block"
                >
                  Detected columns: {importColumns.map((c) => c.label).join(', ')}
                </Typography>
              </Box>
            )}
            {importLoading && (
              <Box display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            )}
            {importPreview && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Preview Summary
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Creates
                    </Typography>
                    <Typography variant="h6">{importSummary.create}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Updates
                    </Typography>
                    <Typography variant="h6">{importSummary.update}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Deactivate
                    </Typography>
                    <Typography variant="h6">
                      {importSummary.deactivate}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Skipped
                    </Typography>
                    <Typography variant="h6">{importSummary.skip}</Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Showing up to five rows per section.
                </Typography>
                {previewSamples.creates.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2">To Create</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ZIP</TableCell>
                          <TableCell>Vertical</TableCell>
                          <TableCell>Territory</TableCell>
                          <TableCell>Calendar</TableCell>
                          <TableCell>Active</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewSamples.creates.map((row) => (
                          <TableRow
                            key={`${row.vertical_id}-${row.zip_code}-${row.source_index}`}
                          >
                            <TableCell>{row.zip_code}</TableCell>
                            <TableCell>{row.vertical_name}</TableCell>
                            <TableCell>{row.territory_name || '-'}</TableCell>
                            <TableCell>{row.calendar || '-'}</TableCell>
                            <TableCell>{row.active ? 'Yes' : 'No'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                {previewSamples.updates.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2">Updates</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ZIP</TableCell>
                          <TableCell>Vertical</TableCell>
                          <TableCell>Changes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewSamples.updates.map((update) => (
                          <TableRow key={update.id}>
                            <TableCell>{update.after.zip_code}</TableCell>
                            <TableCell>{update.after.vertical_name}</TableCell>
                            <TableCell>
                              {Object.entries(update.changes).map(
                                ([field, diff]) => (
                                  <Typography
                                    key={field}
                                    variant="caption"
                                    display="block"
                                  >
                                    {field.replace(/_/g, ' ')}:{' '}
                                    {String(diff.from ?? '—')} →{' '}
                                    {String(diff.to ?? '—')}
                                  </Typography>
                                )
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                {previewSamples.deactivations.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2">To Deactivate</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ZIP</TableCell>
                          <TableCell>Vertical</TableCell>
                          <TableCell>Territory</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewSamples.deactivations.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.zip_code}</TableCell>
                            <TableCell>{row.vertical_name}</TableCell>
                            <TableCell>{row.territory_name || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                {previewSamples.skipped.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2">Skipped Rows</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Reason</TableCell>
                          <TableCell>Row Data</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewSamples.skipped.map((row, index) => (
                          <TableRow key={`${row.reason}-${index}`}>
                            <TableCell>{row.reason}</TableCell>
                            <TableCell>
                              <Typography variant="caption" component="pre">
                                {JSON.stringify(row.row ?? row.source_data ?? {}, null, 2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportDialogClose}>Close</Button>
          <Button
            onClick={handlePreviewImport}
            disabled={importLoading || importApplyLoading}
            startIcon={importLoading ? <CircularProgress size={16} /> : null}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyImport}
            disabled={importApplyLoading || importLoading}
            startIcon={importApplyLoading ? <CircularProgress size={16} /> : null}
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Client Zip</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Client" value={currentZip.client_name} disabled />
            <TextField label="ZIP" value={currentZip.zip} disabled />
            <TextField label="Vertical" value={currentZip.vertical_name} disabled />
            <Autocomplete
              options={territories}
              getOptionLabel={(option) => option.label}
              value={
                territories.find((t) => t.value === currentZip.client_territory) || null
              }
              onChange={(e, val) =>
                handleEditChange('client_territory', val ? val.value : '')
              }
              renderInput={(params) => <TextField {...params} label="Territory" />}
            />
            <TextField
              label="Calendar"
              value={currentZip.calendar || ''}
              onChange={(e) => handleEditChange('calendar', e.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={currentZip.active}
                  onChange={(e) => handleEditChange('active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
